/**
 * api接口统一管理
 */
import http from '@/v2/utils/http'
import { networkData } from '@/v2/utils/data'
import util from '@/v2/utils/util'
import exam_enum from '../../utils/exam_enum'
import { markingData } from '@/v2/utils/fixData'
import markingEnum from "./markingEnum";
import { examData, baseData } from "@/v2/utils/data";

const STS_TOKEN_URL = '/marking-platform/ossUpload/get/fly/sts/token'
const SLS_TOKEN_URL = '/marking-platform/sls/sts'

export const baseApi = {
  getSTSToken: params => http.get(STS_TOKEN_URL, params).then(resp => {
    return {
      region: 'oss-cn-hangzhou',
      bucket: process.env.OSS_BUCKET,
      secure: true,
      accessKeyId: resp.body.accessKeyId,
      accessKeySecret: resp.body.accessKeySecret,
      stsToken: resp.body.securityToken,
      refreshSTSTokenInterval: 1000 * 60 * 40, // 40分钟
      refreshSTSToken: async () => {
        const info = await http.get(STS_TOKEN_URL, params)
        return {
          accessKeyId: info.body.accessKeyId,
          accessKeySecret: info.body.accessKeySecret,
          stsToken: info.body.securityToken
        }
      }
    }
  }),
  getSlsToken: () => http.get(SLS_TOKEN_URL).then(resp => {
    return {
      ...resp.body,
      refreshSTSToken: async () => {
        const info = await http.get(SLS_TOKEN_URL)
        return info.body
      },
      // 刷新临时访问凭证的时间间隔，单位为毫秒。
      refreshSTSTokenInterval: 1000 * 60 * 40
    }
  }),
  // 查询任务进度
  queryTaskProgress: tempId => http.get(`/marking-platform/long-task/${tempId}`),
  getSchoolStageList: schoolId => http.get(`/marking-platform/configurations/regions/${schoolId}/stages`, null), // 获取学校下学段信息
  getSchoolLevels: (schoolId, stage) => http.get(`/marking-platform/configurations/schools/${schoolId}/stages/${stage}/values`), // 获取学段等级信息
  getSixRateConfiguration: params => http.get('/marking-statistics/exam-config/six-rate/school/course', params), // 获取学校六率配置
  getRegionRatioSchoolList: params => http.get('/marking-platform/school-class/list_choose_group_school_class', params),
  checkBuildVersion: () => new Promise((resolve, reject) => {
    if (process.env.NODE_ENV === 'development') {
      reject({ message: '开发环境，不检查版本' })
      return
    }
    http.get(process.env.VERSION_URL + "?t=" + Date.now()).then(version => {
      try {
        const versionStr = JSON.stringify(version)
        const oldVersion = localStorage.getItem('regionBuildVersion')
        if (oldVersion !== versionStr) {
          localStorage.setItem('regionBuildVersion', versionStr)
          location.reload()
        }
        resolve({ message: '版本检查成功', body: version })
      } catch (error) {
        resolve({ message: error.message || '版本检查失败' })
      }
    })
  })
}


export const examApi = {
  getExamStatusByExamIds: params => http.get("/marking-exam-manager/exam-list/status/simple", params),
  getSingleExam: (examId) => http.get(`/marking-exam-manager/exam-list/${examId}`).then(resp => {
    if (resp && resp.body) {
      resp.body.forEach(item => {
        item.type = exam_enum.getCodeByKey(item.type)
        item.hasPaper = exam_enum.getCodeByKey(item.hasPaper)
        item.isMore = exam_enum.getCodeByKey(item.isMore)
        item.examSchools.forEach(school => {
          school.school = item.sysSchools.find(s => s.id === school.schoolId)
          school.examSign = exam_enum.getCodeByKey(school.detailStatus)
        })
        item.reportNum = item.examSchools.filter(s => s.examSign === 2).length
        item.schoolNum = item.schoolIds.length
        item.subject = {
          dicCode: item.subjectCode,
          dicName: item.subjectName,
        }
        item.teamLeaders = item.teachers
        item.teamLeaderIds = item.teachers.map(t => t.id)
      })
      resp.body = resp.body[0]
    }
    return resp
  }),
  copyExam: id => http.post('/marking-exam-manager/exam-list/reuse?examId=' + id),
  recoverExam: id => http.put('/marking-exam-manager/exam-list/recover/' + id),
  recycleExamList: params => http.post('/marking-exam-manager/exam-list/search/deleted', params),
  setTeamLeader: params => http.post('/marking-exam-grading/grading-task/set-team-leader', params), // 设置阅卷组长

  /// 扫描批次相关
  // 获取配置参数接口
  getConfigurationParameter: (params) => http.get(`/marking-exam-manager/scan-batch/config/${params.examId}/${params.schoolId}`),
  // 获取批次列表
  getScanBatchList: (params) => http.post('/marking-exam-manager/scan-batch/list', params),
  // 获取考试扫描进度
  getScanProgress: (params) => http.get(`/marking-exam-manager/scan-batch/progress/${params.examId}/${params.schoolId}`),
  // 根据批次获取原卷列表
  getScanBatchPaperList: (params) => http.post('/marking-exam-manager/scan-batch/paper/list', params),
  // 删除扫描原卷
  delScanBatchPaper: (params) => http.delete(`/marking-exam-manager/scan-batch/paper/delete/ids`, params, 'json'),
  // 删除扫描批次
  delScanBatch: (batchId) => http.delete(`/marking-exam-manager/scan-batch/${batchId}`),
  // 删除试卷
  delStudentPaper: (params) => http.delete(`/marking-exam-manager/student-paper-statistic/delete_paper?examId=${params.examId}${params.url}`, params.resultIds, 'json'),
  // 是否包含选科筛选
  getShowChooseSubject: (examId) => http.get(`/marking-exam-manager/student-paper-statistic/${examId}/choose_subject`),
  // 获取学生试卷集合
  getStudentPaper: (params) => http.post('/marking-exam-manager/student-paper-statistic/student-paper/query', params),
  // 批量标记无名卷
  labelUnnamedVolume: (params) => http.put(`/marking-exam-manager/exam/${params.examId}/doubt/mark_not_find_student/batch`, params.detailIds),
  // 获取疑问项数量
  getExamDoubt: params => http.get(`/marking-exam-manager/exam/${params.examId}`, params),
  // 恢复识别
  recoverScan: (examId, ids) => http.put(`/marking-exam-manager/scan-batch/paper/recover?examId=${examId}`, ids),
  // 重建学生
  reNewStudent: params => http.post('/marking-exam-manager/quick-repair-part-student', params),
  reALLNewStudent: examId => http.post('/marking-exam-manager/quick-repair-student?examId=' + examId, {}),

  anewIdentify: (params) => http.post('/marking-exam-manager/recognition/task', params), // 登分框重识别
  getIdentifyStudentList: params => http.get('/marking-exam-manager/exam_student/need-repair/stud-info', params), // 获取重识别学生列表
  getQuestionList: params => http.post(`/marking-exam-manager/exam/${params.examId}/doubt/search`, params),
  deleteQuestionList: params => http.delete(`/marking-exam-manager/exam/${params.examId}/doubt`, params.doubtIds, "json"), // 删除 疑问项
  updateQuestionMarkNo: params => http.put(`/marking-exam-manager/exam/${params.examId}/doubt/${params.doubtId}/mark_not_find_student`), // 标记无名卷
  updateQuestionList: params => http.put(`/marking-exam-manager/exam/${params.examId}/doubt`, params.doubts),// 修改以问项
  refreshExamSchool: examId => http.put(`/marking-exam-manager/exam-base/region/${examId}/refresh-exam-school`), // 刷新参考学校
  getListAllStand: examId => http.get(`/marking-exam-manager/stand/region/${examId}`).then(resp => {
    examData.setToDoQuestionStand(resp)
    return resp
  }), // 获取所有标准答案(模板一和二)
  getPagePosition: params => http.get('/marking-exam-manager/exam-stand-state/page-positions', params),
  splitExam: (params) => http.post("/marking-exam-manager/split-exam", params), // 拆分考试
  splitExamCheck: (params) => http.get(`/marking-exam-manager/split-exam/${params.examId}/can_split`), // 是否允许拆分考试.
  getObjectivePosition: (params) => http.post('/marking-exam-manager/stand/position/objective', params),
  // 获取Ocr任务
  getOcrTask: schoolId => http.get(`/marking-exam-manager/stand/${schoolId}/re_ocr/task`),
  getSheetTemplate: (examId) => http.get(`/marking-exam-manager/answer-sheet/exam-id/${examId}`),// 获取模板信息
  getStandAnswerUrl: examId => http.put(`/marking-exam-manager/stand/student/${examId}`).then(resp => {
    if (resp.success && resp.body.examStandVos) {
      resp.body.examStandVos.forEach(element => {
        element.choiceTypeCode = exam_enum.getCodeByKey(element.choiceType)
      })
    }
    return resp
  }),
  saveAffirmAllList: (params) => http.put(`/marking-exam-manager/stand/region/${params.examId}`, params), // 确认标准答案
  clearExam: (params) => http.delete('/marking-exam-manager/exam-list/clear_data', params, 'json'), // 清理考试数据
  getScanProgressNew: params => http.post('/marking-exam-manager/student-paper-statistic/student-paper/rce/count', params), // 获取扫描答题卡进度
  cancelAllAbsenceMarks: (params) => http.post("/marking-exam-manager/student-paper-statistic/student-paper/mark-not-absent", params, 'form'),// 取消全部学生的缺考标记
  getQuestion: (params) => http.post('/marking-question/public/question/info/by/question/ids', params),// 获取题目信息
  getExamNotUploadCount: (params) => http.get('/marking-exam-manager/exam-dashboard/unUpload/count', params),
  getReportProgress: params => http.post('/marking-statistics/exam-school-state/examIds', params), // 获取报告生成进度
  createExamGroup: params => http.post('/marking-exam-manager/exam-group/region', params), // 创建考试组
  getExamGroup: examGroupId => http.get(`/marking-exam-manager/exam-group/region/${examGroupId}`), // 获取考试组
  updateExamGroup: params => http.put(`/marking-exam-manager/exam-group/region/${params.examGroupId}`, params), // 修改考试组
  getExamTotalScoreCoefficient: examId => http.get('/marking-statistics/exam-score/single/total-score', { examId }), // 获取考试总分(会乘系数)
  getExamTotalScore: examId => http.get('/marking-exam-manager/stand/' + examId + '/total-score'), // 获取考试总分
  getGroups: params => http.post("/marking-exam-manager/exam-group/region/search", params),
  getSchoolProgress: targetId => http.get(`/marking-exam-manager/exam-group/school/progress/${targetId}`), // 获取学校处理进度
  deleteSingle: (groupId, examIds) => http.delete(`/marking-exam-manager/exam-group/${groupId}/exam`, examIds), // 删除考试组
  setStatisticalSchool: params => http.post('/marking-exam-manager/exam-group/region/update/statistic-flag', params), // 设置参与统计学校
  getGradingProgress: examIds => http.get('/marking-exam-grading/monitor/grading-progress', { examIds: examIds.join(',') }), // 获取批阅进度
  getOnlineExam: (params) => http.get('/marking-exam-manager/exam-list/single/' + params.examId), // 获取单场考试
  listExamTemplate: params => http.post("/marking-exam-manager/answer-sheet/get-unbind-answer-sheet-list", params),
  bindTemplateCard: (params) => http.put(`/marking-exam-manager/answer-sheet/${params.examId}/bind/${params.id}?version=${params.version}`),
  replaceExamTemp: (params) => http.put(`/marking-exam-manager/answer-sheet/exam-id/${params.examId}/rebind/${params.id}?version=${params.version}`), // 更滑考试模板
  setScoreType: (params) => http.put(`/marking-exam-manager/exam-list/${params.examId}/score-flag/${params.scoreFlag}`), // 设置阅卷方式
  checkImport: (params) => http.get(`/marking-exam-manager/exam-list/${params.examId}/hasResult`), // 判断导入考试是否已经录入了成绩
  getAuditExamCount: (schoolId) => http.get(`/marking-exam-manager/exam-base/approving-count/school-id/${schoolId}`), // 获取待审核考试数量
  getAuditExamList: (params) => http.post('/marking-exam-manager/region/exam/list_exam_approval', params), // 获取考试审核列表
  auditExamStatus: (params) => http.post('/marking-exam-manager/region/exam/approve_exam', params), // 审核
  updateQuestionNum: params => http.put(`marking-exam-manager/exam/${params.examId}/doubt/num`, params), // 重置计数
  getExamPaperStatus: (ids) => http.post('/marking-exam-manager/exam-list/exam-paper-status', ids),
  verifyExamNo: (params) => http.post('/marking-exam-manager/exam_student/verify-exam-no', params),
}

// 基础数据api
export const basicApi = {
  // 获取区域下所有学校列表
  getRegionSchools: (regionId) => http.get('/marking-platform/school/regionId', { regionId }).then(resp => {
    if (resp.body) {
      resp.body.map(item => { item.schoolId = item.id })
    }
    return resp
  }),
  // 获取年级列表
  getListGrades: schoolId => http.get('/marking-platform/school/grade/school-id/academic-year', { schoolId }),
  // 根据年级id获取班级
  getListClasses: gradeId => http.get('/marking-platform/school-class/grade-class/gradeId/' + gradeId),
  // 获取学校下的年级科目列表
  getListSubjects: params => http.post(`/marking-platform/configurations/schools/subjects/list`, params),
  // 获取参考学生列表
  getExamStudentList: params => http.post('/marking-exam-manager/exam_student/list', params),
  // 获取参考学校列表
  getExamSchoolList: params => http.get('/marking-exam-manager/exam_student/imported-exam-school', params).then(resp => {
    if (resp.body) {
      for (let index = 0; index < resp.body.length; index++) {
        const element = resp.body[index];
        element.groupName = element.name
        element.school = element.sysSchoolGradeDetails
      }
    }
    return resp
  }),
  // 获取学校学生数据
  getSchoolStudentList: params => http.get('/marking-exam-manager/exam_student/count/school-student', params, 'form'),
  // 同步学生数据
  syncSchoolStudent: params => http.post('/marking-exam-manager/exam_student/import/sum', params),
  // 删除学生信息
  delSchoolStudent: params => http.delete(`/marking-exam-manager/exam_student/delete?examId=${params.examId}`, params.ids),
  // 复用考试列表
  getReuseExamList: params => http.get('/marking-exam-manager/exam-base/reusable', params, 'form'),
  // 复用考生信息
  reuseStudents: params => http.post('/marking-exam-manager/exam_student/import/multiplex', params, 'form'),
  // 复用考试列表（教师）
  getReuseExamListByTeacher: params => http.get('/marking-exam-manager/exam-base/reusable/teacher', params, 'form'),
  // 获取学校教师数据
  getSchoolTeacherList: params => http.post('/marking-exam-manager/exam_teacher/list', params),
  // 获取参考学校列表（教师）
  getExamSchoolListByTeacher: params => http.get('/marking-exam-manager/exam_teacher/imported-exam-school', params, 'form'),
  // 删除教师信息
  delSchoolTeacher: params => http.delete(`/marking-exam-manager/exam_teacher/delete?examId=${params.examId}`, params.ids),
  // 复用教师信息
  reuseTeachers: params => http.post('/marking-exam-manager/exam_teacher/import/multiplex', params, 'form'),
  // 增加单个学生
  addSchoolStudent: params => http.post('/marking-exam-manager/exam_student/single/add', params),
  // 修改单个学生
  updateSchoolStudent: params => http.put('/marking-exam-manager/exam_student/single/edit', params),
  // 下载学生模板
  downloadStudentTemplate: params => http.post('/marking-exam-manager/exam_student/template', params, 'form'),
  // 验证学生信息
  verifyStudent: (examId, params) => http.post(`/marking-exam-manager/exam_student/verify/${examId}`, params),
  verifyStudentLevel: (examId, params) => http.post(`/marking-exam-manager/exam_student/verify-stud-level/${examId}`, params),
  getHistoryYears: params => http.get('/marking-platform/school/upgrade-history', params, 'form'),
  getExamPerms: params => http.post('/marking-platform/user-roles/get-permissions-by-exams?schoolId=' + params.schoolId, params.ids) // 获取考试的角色权限
}

// 阅卷模块
export const networkApi = {
  createMarkTask: (params) => http.post('/marking-exam-grading/grading-task/region/add/default-task', params), // 新增试题组
  updateAssignMode: (params) => http.post('/marking-exam-grading/grading-task/region/set-assign-mode', params), // 设置批阅模式
  updateMarkSchoolList: (params) => http.post('/marking-exam-grading/grading-task/region/school-mode/modify-exam-grading-school', params), // 设置参与批阅的学校
  clearAllTask: (params) => http.post('/marking-exam-grading/clear-score/all/long-task', params), // 清空所有任务
  clearGroupTask: (params) => http.post("/marking-exam-grading/clear-score/group/long-task", params), // 清空题组任务
  updateReview: (params) => http.post('/marking-exam-grading/grading-task/update-review', params), // 更新考试抽样复评状态
  pauseExamGrading: (params) => http.post('/marking-exam-grading/grading-status/pause/group', params), //暂停网络阅卷
  continueExamGrading: (params) => http.post('/marking-exam-grading/grading-status/start/group', params), //继续网络阅卷
  allocationCopy: (params) => http.post('/marking-exam-grading/grading-task/copy', params), // 复制题组
  deleteGroupTask: (params) => http.post('/marking-exam-grading/grading-task/delete/long-task', params), // 删除批阅设置
  delAllTask: params => http.post('/marking-exam-grading/grading-task/delete/all/long-task', params), // 批量删除批阅任务
  updateMarkType: (params) => {
    params.type = markingEnum.GradingTimes[params.type]
    return http.post('/marking-exam-grading/grading-task/modify-type', params)
  }, // 更新题组批阅方式
  updateAssignType: ({ assignType, teachers, type, examId, groupId, schoolId }) => {
    const params = { firstTeachers: [], examId, groupId, schoolId }
    // 如果修改仲裁分配方式，修改judgeType就行了，assignType和firstTeachers可以为空 @xiaoguang
    if (type === 2) {
      params.judgeType = markingEnum.AssignType[assignType]
    } else {
      params.assignType = markingEnum.AssignType[assignType]
      if (teachers && teachers.length > 0) {
        for (let index = 0; index < teachers.length; index++) {
          const { teacherName, teacherId, todoGradingNum } = teachers[index];
          params.firstTeachers.push({ teacherName, teacherId, todoGradingNum })
        }
      }
    }
    return http.post('/marking-exam-grading/grading-task/teacher-mode/modify-assign-type', params)
  }, // 更新题组分配方式
  updateAssignTeacher: (params) => {
    params.schoolId = params.regionId
    if (params.type === 1) {
      params.firstTeachers = params.teachers
    } else {
      params.secondTeachers = params.teachers
    }
    delete params.teachers
    delete params.type
    return http.post('/marking-exam-grading/grading-task/teacher-mode/modify-grading-teacher', params)
  }, // 更新题组批阅老师
  updateSchoolAssignList: (params) => {
    params.assignType = markingEnum.AssignType[params.assignType]
    return http.post('/marking-exam-grading/grading-task/region/school-mode/modify-allocation-school', params)
  }, // 更新学校分配数量
  updateSchoolAssignType: (params) => {
    params.assignType = markingEnum.AssignType[params.assignType]
    // 仲裁的分配方式传参用judgeType
    if (params.type === 2) {
      params.judgeType = params.assignType
      delete params.assignType
    }
    delete params.type
    return http.post('/marking-exam-grading/grading-task/region/school-mode/modify-school-teacher-assign-type', params)
  }, // 设置各校教师批阅方式
  updateSchoolMarkTeachers: (params) => {
    if (params.type === 2) {
      params.secondTeachers = params.teachers
    } else {
      params.firstTeachers = params.teachers
    }
    delete params.type
    delete params.teachers
    return http.post('/marking-exam-grading/grading-task/region/school-mode/modify-school-grading-teacher', params)
  }, // 设置各校批阅教师
  updateSchoolAssignTeacher: (params) => http.post(`/marking-exam-grading/grading-task/school-assign-region-teacher/region/update-switch`, params), // 设置单校分配接口

  // 获取区域下所有学校列表
  getRegionSchools: (regionId, isSimplify) => http.get('/marking-platform/school/regionId', { regionId }).then(resp => {
    if (isSimplify) {
      if (resp.body) {
        let schools = []
        resp.body.forEach(element => {
          schools.push({ schoolId: element.id, schoolName: element.schoolName })
        })
        resp.body = schools
      }
    }
    return resp
  }),
  // 获取年级列表
  queryListGrade: params => http.get('/marking-platform/school/grade/school-id/academic-year', params),
  // 获取年级科目列表
  queryGradeSubjects: params => http.post('/marking-platform/configurations/schools/subjects/list', params).then(resp => {
    if (resp.body) {
      resp.body = networkData.getSubjects(resp.body)
    }
    return resp
  }),
  // 获取老师以及权限列表
  queryTeachers: params => http.post('/marking-platform/teachers/search', params).then(resp => {
    if (resp && resp.body) {
      networkData.getTodoTeacherList(resp.body.items)
    }
    return resp
  }),
  // 获取考试抽样复评设置
  getReview: params => http.get('/marking-exam-grading/grading-task/get-review', params, 'form'),
  startWuhen: (params) => http.post('/marking-exam-grading/grading-status/start/main', params), // 开始批阅
  pauseWuhen: (params) => http.post('/marking-exam-grading/grading-status/pause/main', params), // 暂停批阅
  endWuhen: (params) => http.put('/marking-exam-grading/grading-status/finish', params, 'form'), // 结束批阅并生成报告
  getAssignMode: (examId, schoolId) => {
    if (!examId || !schoolId) {
      alert(location.href)
      return Promise.resolve({})
    }
    return http.get('/marking-exam-grading/grading-task/get-assign-mode', { examId, schoolId }) // 进入阅卷分工页面前先判断当前模式
  },
  getMarkTaskList: (params, mode) => {
    const assignMode = mode.toLowerCase()
    let url = '/marking-exam-grading/grading-task/region/school-mode/tasks'
    if (assignMode === 'teacher') {
      url = '/marking-exam-grading/grading-task/teacher-mode/task-list'
    }
    return http.post(url, params).then(resp => {
      if (resp && resp.body) {
        try {
          markingData.fixWuHenSet(resp)
          return resp
        } catch (error) {
          console.log(error)
        }
      }
      return resp
    })
  }, // 批阅任务列表含考试信息
  getMarkTeacher: (params) => http.post('/marking-exam-grading/grading-task/teacher-mode/grading-teacher', params),
  getTaskGroupList: (params) => http.post('/marking-exam-grading/grading-task/region/school-mode/group/task', params), // 批阅任务列表
  getGradingCount: (params) => http.post('/marking-exam-grading/grading-task/region/school-mode/group/school-first-grading-count', params), // 批阅学校的已批数量
  getTeacherModeTask: params => http.post('/marking-exam-grading/grading-task/region/teacher-mode/task-info', params), // 单题的详细信息，主要用于修改分工时获取最新的数据
  getSchoolModeTask: params => http.post('/marking-exam-grading/grading-task/region/school-mode/task-info', params), // 单题的详细信息，主要用于修改分工时获取最新的数据
  getWuHenRole: (params) => http.get('/marking-exam-grading/exam/grad/grading/role', params).then(resp => {
    if (resp.body) {
      resp.tabs = networkData.getRoleMenus(resp.body)
    }
    return resp
  }),

  getTeacherGuide: (params) => http.post('/marking-main/exam/grad/get_teacher_guide', params, 'form'), //查询老师是否看过引导
  setTeacherGuide: (params) => http.post('/marking-main/exam/grad/set_teacher_guide', params), //更新老师操作引导
  getQuestionItem: params => http.post('/marking-exam-grading/grading/question/item', params).then(resp => {
    if (resp.body) {
      const { toDoQuestions, toDoItem } = networkData.getTodoQuestions(resp.body, params.num)
      resp.toDoQuestions = toDoQuestions
      resp.toDoItem = toDoItem
    }
    return resp
  }), // 获取题块信息
  getStudentMosaicPaper: (params) => http.put(`/marking-exam-manager/student-position/region/mosaic/url/${params.examId}/${params.resultId}?url=${params.url}`), // 获取学生试卷(打码)
  updateTeaGrading: (params) => http.post('/marking-main/exam/grad/update_tea_grading', params), // 变更教师批阅
  listSubjectStand: (params) => http.get(`/marking-exam-manager/stand/${params.examId}/subjective`), // 获取主观题标准答案
  getQuestionSchedule: params => http.get('/marking-exam-grading/monitor/teacher-grading-count', params),// 获取批阅进度
  checkStatus: (params) => http.post('/marking-main/exam/grad/detail/check_status', params), // 校验详情生成状态
  generateDetail: (params) => http.post('/marking-main/exam/grad/detail/generate', params), // 生成详情 TODO:接口存在问题，暂时返回空
  getSchedule: (params) => http.post('/marking-main/exam/grad/detail/get_schedule', params), // 生成详情
  getExamStatus: params => http.get("/marking-exam-grading/exam/grad/get-status", params), // 获取考试状态
  checkIfStandAnswer: (params) => http.get('/marking-exam-grading/grading-task/check-stand-and-doubt', params), // 检查是否确认标准答案
  queryNetWorkList: (params) => http.post('/marking-exam-grading/exam-list/region/exams', params), // 查询网络阅卷任务
  queryRegionNetWorkList: (params) => http.post('/marking-exam-grading/exam-list/region/issued/exams', params), // 查询市下发的网络阅卷任务
  getTeacherNetWorkList: (params) => http.post('/marking-exam-grading/exam-list/region/teacher/exams', params), // 查询网络阅卷任务
  gradingPreviewList: params => http.get("/marking-exam-grading/exam/grad/grading-preview", params),
  getReadOverHistory: params => http.post("/marking-exam-grading/grading/read-over/history", params).then(resp => {
    if (resp.body.items) {
      networkData.getTodoQuestions2(resp.body.items)
    }
    return resp
  }),
  getHistoryUpdate: params => http.post('/marking-main/exam/grad/read_over/history/update', params),
  readOver: params => http.put('/marking-exam-grading/grading/question/item', params), // 添加分值
  updateReadOver: (params) => http.put('/marking-exam-grading/grading/question/item/update', params), // 修改批阅
  updateReadMark: async (params, ques) => {
    if (!params.examId) {
      alert(location.href)
      return Promise.resolve({})
    }
    await networkData.setExamMarkVos(params, ques)
    return http.put('/marking-exam-grading/exam-grading/mark', params)
  },
  getReadMark: (params, ques, isSetMark = true) => {
    return http.get('/marking-exam-grading/exam-grading/mark', params).then(resp => {
      if (resp.body && isSetMark) {
        networkData.setImageMarks(ques, resp.body)
      }
      return resp
    })
  },
  updateReadOverHistory: params => http.put("/marking-exam-grading/grading/read-over/history", params),

  // 批阅监控
  getProgressByQuestion: (params) => http.get('/marking-exam-grading/monitor/region/question', params).then(res => {
    if (res.body) {
      networkData.setQuesProgressList(res)
    }
    return res
  }),
  getProgressByTeacher: (params) => http.get('/marking-exam-grading/monitor/region/teacher', params),
  getProgressBySchool: (params) => http.get('/marking-exam-grading/monitor/region/school', params).then(res => {
    if (res.body) {
      networkData.setSchoolProgressList(res)
    }
    return res
  }),
  getExamMarkProgress: (params) => http.get('/marking-exam-grading/monitor/region/progress', params),
  getExamMarkProgress: (params) => http.get('/marking-exam-grading/monitor/region/progress', params),
  getMarkingSchoolDetail: (params) => http.get('/marking-exam-grading/monitor/region/school', params).then(res => {
    if (res.body) {
      networkData.setProgressDetail(res)
    }
    return res
  }), // 获取批阅监控详细
  getMarkingQuestionDetail: (params) => http.get('/marking-exam-grading/monitor/region/question', params).then(res => {
    if (res.body) {
      networkData.setProgressDetail(res)
    }
    return res
  }), // 获取批阅监控详细
  getExamQuestions: params => http.get(`/marking-exam-grading/grading-teacher/stands`, params, "form"), // 获取考试题目
  getreadQuality: (params) => http.get('/marking-exam-grading/monitor/grading-quality', params).then(resp => {
    return networkData.getReadQuality(resp)
  }),
  getReviewSuperVision: params => http.post(`/marking-exam-grading/monitor/review-supervision`, params), // 获取抽样复评数据
  getScoreSpread: (params) => http.get('/marking-exam-grading/monitor/score-distribution', params).then(resp => {
    return networkData.getScoreSpread(resp)
  }),
  checkExportScoreDistribution: params => http.get('/marking-main/exam/grad/check_export_score_distribution', params), // 导出评分分布
  // 获取阅卷分数分布
  getScoreSpreadCity: (params) => http.get('/marking-main/exam/grad/get_score_distribution/city', params).then(resp => {
    if (resp.result && resp.result.distribution_table) {
      networkData.setTeacherNameIfEmpty(resp.result.distribution_table)
    }
    return resp
  }),
  getSchoolList: (params) => http.post('/marking-platform/regions/schools/search/relation-schools', params),
  // 获取阅卷质量
  getreadQualityCity: (params) => http.get('/marking-main/region/exam/grad/get_marking_quality/city', params).then(resp => {
    if (resp.result && resp.result.statisticsTable && resp.result.statisticsTable.statisticsRows) {
      networkData.setTeacherNameIfEmpty(resp.result.statisticsTable.statisticsRows)
      networkData.setTeacherNameIfEmpty(resp.result.avgChart.teacherScore)
    }
    return resp
  }),
  getSchoolAll: (examId) => http.get(`/marking-exam-manager/exam-schools/examId/${examId}/with/schoolName`), // 获取全部学校数据-根据考试id
  getMonitorSchool: (params) => http.get('/marking-exam-grading/monitor/exam-schools', params), // 获取批阅监控数据
  getGradingInfo: (params) => http.get('/marking-exam-grading/monitor/grading_info', params),
  getRegionAll: (params) => http.get('/marking-main/region/exam/grad/assign/grading_region', params), // 查询所有的区域
  clearQuestionScore: (params) => http.post("/marking-exam-grading/clear-score/group/by-judge-type", params), // 清空题目分值
  clearTeacherScore: (params) => http.post('/marking-exam-grading/clear-score/group/by-teacher', params), // 清空教师分值
  clearSchool: (params) => http.post('/marking-main/region/exam/grad/clear_score/school', params), // 清除的学校
  // 查询抽样复评监测的老师
  getMarkingTeacher: params => http.post('/marking-main/exam/grad/get_all_tea_question', params, 'form').then(resp => {
    const teachers = []
    if (resp && resp.result) {
      if (resp.result.reviewSupervisionTeacherVos) {
        resp.result.reviewSupervisionTeacherVos.forEach(item => {
          teachers.push({ teacherId: item.teacherId, teacherName: item.teacherName })
        })
      }
      return { success: true, body: teachers }
    }
    return { success: false, body: teachers, message: resp.message }
  }),
  // 查询老师批阅过的题号
  getMarkingQuestion: params => http.post('/marking-main/exam/grad/get_marking_question', params, 'form').then(resp => {
    const questions = []
    if (resp && resp.result) {
      resp.result.forEach(item => {
        questions.push({ bigNum: item.bigNum, num: item.num, smallNum: item.smallNum })
      })
      return { success: true, body: questions }
    }
    return { success: false, body: questions, message: resp.message }
  }),
  // 查询抽样复评批阅监控
  getReviewAndReviewReport: params => http.post('/marking-main/exam/grad/get_review_supervision', params).then(resp => {
    const body = { total: 0 }
    if (resp && resp.result) {
      body.total = resp.result.total
      if (resp.result.currentDate) {
        body.updated_at = util.formatDate(resp.result.currentDate, 'ymdhis')
      } else {
        body.updated_at = ''
      }
      body.result = resp.result.examReviewSupervisionResultVos
    }
    return { success: false, body: body, message: resp.message }
  }),
  // 查询抽查试题
  getSpotCheckQuestion: (params) => http.post('/marking-main/exam/grad/grading/random/check/question', params).then(resp => {
    const questions = []
    if (resp && resp.result) {
      resp.result.forEach(item => {
        questions.push({ bigNum: item.bigNum, num: item.num, smallNum: item.smallNum })
      })
      return { success: true, body: questions }
    }
    return { success: false, body: questions, message: resp.message }
  }),
  // 查询抽查老师
  getSpotCheckTeacher: (params) => http.get('/marking-exam-grading/grading-teacher/all', params, 'form').then(res => {
    if (res.success && res.body.length > 0) {
      for (let index = 0; index < res.body.length; index++) {
        const element = res.body[index];
        element.nickName = (element.nickName ? element.nickName : element.id.substring(element.id.length - 6) + '(已删除)') + '(' + element.schoolName + ')'
      }
    }
    return res
  }),
  // 抽查试卷
  getSpotCheckExam: (params) => http.get('/marking-exam-grading/monitor/grading-detail-by-random', params, 'form'),
  getReadAvg: params => http.get('/marking-exam-grading/grad/score/avg', params),
  getAllGradingTeacher: (examId, schoolId) => http.get('/marking-exam-grading/grading-teacher/all', { examId, schoolId }), // 获取发送批阅通知教师列表 - 全部
  sendGradingSMS: (examId, schoolId, teacherIds) => http.put(`/marking-exam-grading/notice/grad?examId=${examId}&schoolId=${schoolId}&teacherIds=${teacherIds}`), // 发送批阅通知短信
  getTeacherGradingInfo: params => http.get('/marking-exam-grading/grading-teacher/grading-nums', params),// 获取教师分工信息
  seAllAssignByTeacher: params => http.post('/marking-exam-grading/grading-task/teacher-mode/batch-modify-assign-type', params), // 区域教师模式，批量设置阅卷分配方式
  seAllTypeBySchool: params => http.post('/marking-exam-grading/grading-task/region/batch-modify/type', params), // 区域学校模式，批量设置单双批
  setAllAllocationBySchool: params => http.post('/marking-exam-grading/grading-task/region/school-mode/batch-modify-allocation-school', params), // 区域学校模式，批量设置学校间分配方式
  backGradingInfo: params => http.delete('/marking-exam-grading/grading/question/item/delete-and-callback', params, 'json'), // 回退批阅信息
  releaseTimeoutTask: params => http.put('/marking-exam-grading/grading/question/item/release', params, 'form'),//释放超时任务
  getGradingStatus: params => http.get('/marking-exam-grading/grading-status/get-grading-status', params),// 获取指定任务的批阅状态,YES是暂停，NO是未暂停
  copyMarkTask: params => http.post('/marking-exam-grading/grading-task/teacher-mode/copy-task', params),//复制批阅任务
  addWuhenSet: params => {
    markingData.trySetParams(params)
    return http.post('/marking-exam-grading/grading-task/teacher-mode/add-task', params)
  },
  updateMuliSubject: params => http.put('/marking-statistics/multi-exam/update/multiple/exam', params), // 修改统计科目，会触发考试报告重新生成
  multiReportState: id => http.get(`/marking-statistics/multi-exam/${id}/simple`),
}

// 系统管理
export const systemApi = {
  getParamRouters: () => {
    const menus = JSON.parse(window.YJ_REGION_STORAGE.getItem('menus'))
    const routers = []
    for (let item of menus) {
      if (item.permCode === 'Region-System' || item.permCode === 'City-System') {
        for (let ol of item.children) {
          if (ol.permCode === 'Region-System-Parm' || ol.permCode === 'City-System-Parm') {
            for (let li of ol.children) {
              let obj = { path: li.moduleUrl, name: li.permName }
              routers.push(obj)
            }
          }
        }
      }
    }
    return Promise.resolve({ body: routers })
  },
  getRegionUser: params => http.post('/marking-platform/teachers/search', params),//获取区域人员列表
  updateUserStatus: params => http.put(`/marking-platform/teachers/${params.id}/status/${params.status}`, {}), //更新状态
  getRegionRoleList: (params) => http.get('/marking-platform/roles/schools/' + params.schoolId + '/roles', { scope: 0 }),//获取区域角色列表
  //角色管理
  getPermList: (params) => http.post('/marking-platform/permissions/templates-type/' + params.schoolType, []),//获取已有菜单权限模板
  updatePerm: (params) => http.put(`/marking-platform/permissions/schools/${params.schoolId}/roles/${params.roleId}`, params.perms),//更新角色权限
  getPerm: (params) => http.get(`/marking-platform/permissions/schools/${params.schoolId}/roles/${params.roleId}`, {}),//获取角色权限
  getSchoolList: (params) => http.postXkw('/marking-platform/regions/schools/search/relation-schools', params),//获取学校列表
  getGroupList: (params) => http.get('/marking-platform/regions/schools/groups', params),//获取分组列表
  setCityGroup: params => http.put('/marking-platform/regions/schools/city-groups/move', params), //移至分组
  getCityGroup: (params) => http.get('/marking-platform/regions/schools/city-groups', params),//获取分组列表
  moveToGroup: (params) => http.put('/marking-platform/regions/schools/groups/move', params),//获取分组列表
  addGroup: (params) => http.post('/marking-platform/regions/schools/groups', params, 'form'),//新增分组
  updateGroup: (params) => http.put('/marking-platform/regions/schools/groups', params, 'form'),//编辑分组
  deleteGroup: (params) => http.delete('/marking-platform/regions/schools/groups', params, 'form'),//删除分组
  delCityGroup: (params) => http.delete('/marking-platform/regions/schools/cities/groups', params, 'form'),//删除分组
  getGroupSchoolList: (params) => http.get(`/marking-platform/regions/schools/groups/${params.groupId}/schools`, { schoolId: params.schoolId }),//获取分组下的学校
  getRegionSchoolNum: (params) => http.get('/marking-platform/regions/schools/count', params),//查询区域学校总数
  getCitySchoolCount: (params) => http.get('/marking-platform/regions/schools/city-schools/count', params),//查询市学校总数
  listCityGroupSchool: params => http.get(`/marking-platform/regions/schools/city-groups/${params.groupId}/schools`, { schoolId: params.schoolId }), //查询市级组下的学校
  getStudentList: (params) => http.postXkw('/marking-platform/students/search', params),//获取学生列表
  queryTeacher: (params) => http.postXkw('/marking-platform/teachers/search', params), // 获取教师列表
}

export const configApi = {
  // 通过学校id获取年级列表
  getSchoolGrades: schoolId => http.get(`/marking-platform/school/grade/schoolId/${schoolId}`),
  // 根据学校，学段获取科目
  getStageSubjects: (schoolId, stage) => http.get(`/marking-platform/configurations/schools/${schoolId}/stages/${stage}/subjects`),
  // 根据学校，年级获取科目
  getGradeSubjects: (params, isFilter) => http.post(`/marking-platform/configurations/schools/subjects/list`, params).then(resp => {
    if (resp.body) {
      return baseData.getTodoSubjects(resp.body)
    }
    return resp
  }),
  // 获取其他配置
  getOther: (schoolId) => http.get(`/marking-platform/configurations/schools/${schoolId}/platformTypes/REGION/others`).then(resp => {
    baseData.setConfigOptions(resp)
    return resp
  }),
  // 更新参数数值
  update: params => http.put(`/marking-platform/configurations/values`, params),
  getRegionStages: schoolId => http.get(`/marking-platform/configurations/regions/${schoolId}/stages`),
  // 获取区域高频错题，薄弱知识配置
  getRegionWeakKnp: (schoolId, stage) =>  http.post('/marking-platform/configurations/getByParam', {
    schoolId,
    stage,
    configGroup: "EXAM_WEAK"
  }),
  // 根据学校，年级获取科目下面得分等级
  getSchoolLevels: (schoolId, grade, courseId) => http.get(`/marking-platform/configurations/schools/${schoolId}/grades/${grade}/values?courseId=${courseId}`),
  // 初始化学校的配置
  initSchool: schoolId => http.post(`/marking-platform/configurations/schools/${schoolId}`),
  // 获取统计配置
  getStatistics: schoolId => http.post('/marking-platform/configurations/getByParam', { schoolId, configGroup: "EXAM_ANALYSE" }).then(resp => {
    baseData.setConfigOptions(resp)
    return resp
  }),
  // 根据类型列表获取配置详情
  getConfigByTypes: (schoolId, types) => http.post(`/marking-platform/configurations/schools/${schoolId}/configTypes`, types).then(resp => {
    baseData.setConfigOptions(resp)
    return resp
  }),
  // 获取学科列表
  getSubjectLevels: params => http.post('/marking-platform/configurations/school/stage/course/level/values', params).then(resp => {
    if (resp.body.showLevelConfigs) {
      baseData.setOptionList(resp.body.showLevelConfigs)
    }
    return resp
  }),
  updateSubjectLevels: params => http.put('/marking-platform/configurations/values/updateByCourse', params)
}

//考试报告
export const reportApi = {
  getArtTotal: (levelExamId, schoolId) => http.get(`/marking-statistics-report/region/art/summary/total/${levelExamId}/${schoolId}`),
  getArtMap: (levelExamId, schoolId) => http.get(`/marking-statistics-report/region/art/summary/map/${levelExamId}/${schoolId}`),
  getScienceTotal: (levelExamId, schoolId) => http.get(`/marking-statistics-report/region/science/summary/total/${levelExamId}/${schoolId}`),
  getScienceMap: (levelExamId, schoolId) => http.get(`/marking-statistics-report/region/science/summary/map/${levelExamId}/${schoolId}`),

  getReadMarkExportList: params => http.post(`/marking-statistics-download/grading/task/list/exam/${params.examId}/school/${params.schoolId}`, params.types), // 获取批阅报告导出列表
  createReadMarkExport: params => http.post('/marking-statistics-download/grading/task/save', params),// 创建批阅报告
  refreshReadMarkExport: params => http.post('marking-statistics-download/grading/task/refresh', params), // 刷新批阅导出报告
  getReportExportList: params => http.post(`/marking-statistics-download/report/task/list/exam/${params.examId}/school/${params.schoolId}`, params.types),// 获取年级报告导出列表
  getExamReportExportList: (params) => http.get(`/marking-statistics-download/report/task/list/exam/${params.examId}/school/${params.schoolId}/type/${params.type}`).then(resp => {
    if (resp.success && resp.body) {
      const list = []
      resp.body.forEach(item => {
        list.push({
          id: item.id,
          CreationTime: util.formatDate(item.createTime, 'ymdhis'),
          examId: item.examId,
          filename: item.fileName,
          schoolId: item.schoolId,
          state: item.state ? Number(item.state) : '',
          url: item.url,
          param: item.category,
          childTypes: item.paramModel.childTypes
        })
      })
      return { success: true, body: list }
    }
    return resp
  }),
  createReportExport: params => http.post(`/marking-statistics-download/report/task/save`, params),// 创建年级导出报告
  refreshReportExport: params => http.post('/marking-statistics-download/report/task/refresh', params), // 刷新年级导出报告
  getGradeBasicIndicatorsTarget: examId => http.get(`/marking-statistics/exam-config/basic-index/region/${examId}`), // 获取年级基础指标配置
  getBaseAnalysis: params => http.get('/marking-statistics-report/single/basic/index/region/all/school', params),// 获取基础指标分析数
  getAcademicLevelList: params => http.post(`/marking-statistics-report/single/region/school/basic/info/all/school/academic/level/statistics?regionId=${params.schoolId}&examId=${params.examId}`), // 获取学业等级分布
  createBasicIndicatorsReport: params => http.post('/marking-statistics-download/report/task/upsert', params),//创建基础指标导出
  getSchoolStability: params => http.get(`/marking-statistics-report/single/region/school/basic/info/all/school/quartile/analysis`, params), // 获取四分位
  getSchoolGradeStability: params => http.get(`/marking-statistics-report/single/school/${params.type}/basic/info/all/class/quartile/analysis`, params), //四分位数
  getCitySchoolStability: params => http.get('/marking-statistics-report/single/city/region/basic/info/all/region/quartile/analysis', params), //四分位
  getItemAnalysisList: params => http.get(`/marking-statistics-report/question-contrastive/option/region/sub-school`, params), // 获取学校试题分析
  getSchoolClassItemAnalysisList: params => http.get(`/marking-statistics-report/question-contrastive/option/region/sub-class`, params), // 获取学校班级试题分析
  startExportTask: params => http.post('/marking-statistics-download/report/task/upsert', params), // 触发导出报告任务
  checkExportTask: (examId, schoolId, types) => http.post(`/marking-statistics-download/report/task/list/exam/${examId}/school/${schoolId}`, types),
  getRegionMultipleBase: params => http.get('/marking-statistics-report/multiple/basic/index/region/all/subject', params),//查询区域年级各科基础指标分析
  getMultipleBasicIndicators: params => http.get('/marking-statistics-report/multiple/basic/index/region/final/summary/all/school', params),//获取多科基础指标
  getRegionClassCompares: params => http.get('/marking-statistics-report/single/basic/index/region/all/class', params),
  studentRanking: (type, params) => http.post(`/marking-statistics-report/student-ranking/${type}`, params),// 查询学校学生成绩排名
  getShowRanking: params => http.get('/marking-platform/configurations/show/ranking/course', params),// 获取是否显示排名
  studentCityRankingByMultiple: (params) => http.post('/marking-statistics-report/multiple/student-ranking/city', params),// 查询区域学生成绩排名
  studentRegionRankingByMultiple: (params) => http.post('/marking-statistics-report/multiple/student-ranking/region', params),// 查询学校学生成绩排名
  getComparisonConfig: examId => http.get(`/marking-statistics/exam-config/comparison/${examId}/user`),// 获取学校任意考试对比分析配置
  saveComparisonConfig: params => http.post(`/marking-statistics/exam-config/comparison/${params.examId}/user`, params.examIds),// 更新学校任意考试对比分析配置
  studentCityRankingByMultiple: (params) => http.post('/marking-statistics-report/multiple/student-ranking/city', params),// 查询区域学生成绩排名
  studentRegionRankingByMultiple: (params) => http.post('/marking-statistics-report/multiple/student-ranking/region', params),// 查询学校学生成绩排名
  getSubjectTop10: finalExamId => http.get('/marking-statistics-report/multiple/student-ranking/region/top_one/group_by_course', { finalExamId }), // 前10名各科成绩详情
  getMultipleBasicAnalysis: params => http.get('/marking-statistics-report/multiple/basic/index/region/all/subject', params),//获取多科基础指标
  getMultipleBasicAnalysisByCity: params => http.get('/marking-statistics-report/multiple/basic/index/city/final/summary/all/region', params),//获取多科基础指标
  getMultipleBasicAnalysisBySchool: params => http.get('/marking-statistics-report/multiple/basic/index/region/final/summary/all/school', params),//获取多科基础指标
  getRegionQuestionScoreList: (params) => http.get('/marking-statistics-report/question-contrastive/kqa/region', params), // 小题详细分析
  getCityQuestionScoreList: (params) => http.get('/marking-statistics-report/question-contrastive/kqa/city', params), // 小题详细分析
  regionMultiple343: (params) => http.get('/marking-statistics-report/multiple/student-ranking/region/school/student/343', params), // 区域全区年级多科总分3-4-3各校对比分析
  regionSchoolGoodBadSubject: (params) => http.get('/marking-statistics-report/multiple/subject-ranking/school', params), //区域各校年级优劣势学科分析
  getSchoolMultipleStability: (params) => http.get('/marking-statistics-report/multiple/quartile/area/all/school', params), //四分位
  getSubjectMultipleStability: (params) => http.get('/marking-statistics-report/multiple/quartile/area/all/subject', params), //四分位
  getGradeStability: (params) => http.get('/marking-statistics-report/multiple/quartile/school/all/subject', params), // 四分位
  getSchoolSubjectStability: (params) => http.get('/marking-statistics-report/multiple/quartile/area/all/subject', params), // 四分位
  cityMultipleSchoolScoreSection: (params) => http.post('/marking-statistics-report/multiple/score_section/school', params), // 多科报告成绩区间报表
  cityMultipleScoreSection: (params) => http.post('/marking-statistics-report/multiple/score_section/region', params), // 多科报告成绩区间报表
  setRegionGroupAnalysis: (examId, regionId, params) => http.post(`/marking-statistics/exam-config/custom/school/group?examId=${examId}&regionId=${regionId}`, params), //添加分组分析
  getRegionGroupAnalysisList: (params) => http.get("/marking-statistics-report/custom/school/group/analysis", params), //获取分组分析列表
  regionMultiSchoolRanking: (params) => http.get('/marking-statistics-report/multiple/ranking-section/school', params), //区域各校名次对比
  kqaStatistics: (params) => http.post('/marking-statistics-report/kqa/region', params),// 知识点、题型、能力得分情况
  kqaLevels: (params) => http.get('/marking-statistics-report/kqa/level', params), // 知识点分段数据
  paperAnalysis: (params) => http.get('/marking-statistics-report/paper/analysis', params), // 试卷分析
  bitRanking: (params) => http.get('/marking-statistics-report/multiple/ranking/bit/area/school/analysis/ranking', params), // 名次段人数统计
  getCityQuestionAnalysis: (params) => http.get('/marking-statistics-report/question-contrastive/option/city/sub-region', params), //市逐题分析
  getCitySchoolQuestionAnalysis: (params) => http.get('/marking-statistics-report/question-contrastive/option/city/sub-school', params), //区域逐题分析
  getSchoolQuestionAnalysis: (params) => http.post("/marking-statistics-report/kqa/region", params),// 查询区域题型、知识点、能力得分情况
  getPointAnalysis: () => http.get('/marking-statistics-report/kqa/level'), // 获取知识点分析层级
  getRegionQuestionAnalysis: (params) => http.post("/marking-statistics-report/kqa/city", params),// 查询区域题型、知识点、能力得分情况
  exportBigReport: (params) => http.post('/marking-statistics-download/grand_joint_exam/upsert', params), // 导出大联考报告
  getBigReportExportList: (params) => http.get(`/marking-statistics-download/grand_joint_exam/exam/${params.examId}/school/${params.schoolId}`), // 获取大联考报告导出列表
  getBigReportPaperAnalysis: (params) => http.get('/marking-statistics-report/paper/multiple/paper/analysis', params), // 获取大联考报告试卷命题质量
  getCityMultipleAnalysisByRatio: (params) => http.get('/marking-statistics-report/multiple/ranking/bit/city/region/analysis/bit', params), // 按比例分析
  getCityMultipleSchoolAnalysisByRatio: (params) => http.get('/marking-statistics-report/multiple/ranking/bit/area/school/analysis/bit', params), // 按比例分析
  getCityMultipleAnalysisByScore: (params) => http.get('/marking-statistics-report/multiple/ranking/bit/city/region/analysis/score', params), // 按分数分析
  getCityMultipleSchoolAnalysisByScore: (params) => http.get('/marking-statistics-report/multiple/ranking/bit/area/school/analysis/score', params), // 按分数分析
  getCityMultipleAnalysisByRank: (params) => http.get('/marking-statistics-report/multiple/ranking/bit/city/region/analysis/ranking', params), // 按名次分析
  getCityMultipleSchoolAnalysisByRank: (params) => http.get('/marking-statistics-report/multiple/ranking/bit/area/school/analysis/ranking', params), // 按名次分析
  getSummarySchoolRankCompare: (levelExamId, regionId, params) => http.get(`/marking-statistics-report/region/summary/rankingComparison/${levelExamId}/${regionId}`, params), // 各校名次对比
  getSummarySchoolRegionRankCompare: (levelExamId, cityId, params) => http.get(`/marking-statistics-report/city/summary/rankingComparison/${levelExamId}/${cityId}`, params), // 各校名次对比(区县)
  getSummarySchoolScoreCompare: params => http.get("/marking-statistics-report/region/summary/ranking-and-bit/analysis/score", params), // 各校分数对比(区县)
  getSummarySchoolRegionScoreCompare: params => http.get("/marking-statistics-report/city/summary/ranking-and-bit/analysis/score", params), // 各校分数对比
  getSummarySchoolSectionCompare: (levelExamId, regionId, params) => http.get(`/marking-statistics-report/region/summary/sectionComparison/${levelExamId}/${regionId}`, params), // 各班总分分数段对比
  getCityMultipleSetting: params => http.get('/marking-statistics/ranking-and-bit/config/list', params), // 获取多科报告名次设置
  updateCityMultipleSetting: params => http.post('/marking-statistics/ranking-and-bit/config/update', params), // 更新多科报告名次设置
  getSummarySchoolRatioCompare: params => http.get("/marking-statistics-report/region/summary/ranking-and-bit/analysis/bit", params), // 各校比例对比
  getSummaryStudentRank: params => http.post("marking-statistics-report/region/summary/pageReportCard", params),// 学生成绩单
  getMainRankCompare: (levelExamId, regionId, params) => http.get(`marking-statistics-report/region/main/rankingComparison/${levelExamId}/${regionId}`, params), // 各校名次对比
  getMainScoreCompare: params => http.get("/marking-statistics-report/region/main/ranking-and-bit/analysis/score", params), // 各校分数对比
  getMainRatioCompare: params => http.get("/marking-statistics-report/region/main/ranking-and-bit/analysis/bit", params), // 各校比例对比
  getMainRegionRatioCompare: params => http.get("/marking-statistics-report/city/main/ranking-and-bit/analysis/bit", params), // 各校比例对比(区县)
  getMainSectionCompare: (levelExamId, regionId, params) => http.get(`/marking-statistics-report/region/main/sectionComparison/${levelExamId}/${regionId}`, params),// 各班总分分数段对比
  getMainRegionSectionCompare: (levelExamId, cityId, params) => http.get(`/marking-statistics-report/city/main/sectionComparison/${levelExamId}/${cityId}`, params), // 各班总分分数段对比(区县)
  getScienceClassLevelCompare: (levelExamId, regionId, params) => http.get(`/marking-statistics-report/region/science/levelComparison/${levelExamId}/${regionId}`, params), // 各班单科等级占比分析
  getScienceClassRankCompare: (levelExamId, regionId, params) => http.get(`/marking-statistics-report/region/science/rankingComparison/${levelExamId}/${regionId}`, params), // 各班主科年级名次对比
  getScienceClassScoreCompare: (levelExamId, regionId, params) => http.get(`/marking-statistics-report/region/science/sectionComparison/${levelExamId}/${regionId}`, params), // 各班总分分数段对比
  getScienceStudentRank: params => http.post("marking-statistics-report/region/science/pageReportCard", params), // 学生成绩单
  getArtsClassLevelCompare: (levelExamId, regionId, params) => http.get(`/marking-statistics-report/region/art/levelComparison/${levelExamId}/${regionId}`, params), // 各班单科等级占比分析
  getArtsClassRankCompare: (levelExamId, regionId, params) => http.get(`/marking-statistics-report/region/art/rankingComparison/${levelExamId}/${regionId}`, params), // 各班主科年级名次对比
  getArtsScoreCompare: (levelExamId, regionId, params) => http.get(`/marking-statistics-report/region/art/sectionComparison/${levelExamId}/${regionId}`, params), // 各班总分分数段对比
  getArtsStudentRank: params => http.post("/marking-statistics-report/region/art/pageReportCard", params), // 学生成绩单  xxxxx
  getSummarySchoolRegionRatioCompare: params => http.get("/marking-statistics-report/city/summary/ranking-and-bit/analysis/bit", params), // 各校比例对比(区县)
  getMainRegionRankCompare: (levelExamId, cityId, params) => http.get(`/marking-statistics-report/city/main/rankingComparison/${levelExamId}/${cityId}`, params), // 各校名次对比(区县)
  getMainRegionScoreCompare: params => http.get("/marking-statistics-report/city/main/ranking-and-bit/analysis/score", params), // 各校分数对比(区县)
  generateReport: (params) => http.post('/marking-statistics/statistics/single/initiative/send', params, 'form'), // 生成报告
  getExamSchoolList: (params) => http.get('/marking-platform/regions/schools/exam-schools', params).then(resp => {
    if (resp.body) {
      for (let index = 0; index < resp.body.length; index++) {
        const element = resp.body[index];
        element.groupName = element.name
        element.school = element.sysSchoolGradeDetails
      }
    }
    return resp
  }),//创建考试查询学校
  createGroupReport: params => http.post("/marking-exam-manager/exam-group/report/add", params, 'form'), // 生成多科报告
  getScoreLevelStudentList: (params) => http.get('/marking-statistics-report/student-ranking/list/by/area/ranking', params), // 分析相关学生
  getRatioSetting: (params) => http.get('/marking-statistics/exam-config/question/score/ratio', params),//举一反三解析
  setScoreSection: (params) => http.post(`/marking-statistics/exam-config/score/section?schoolId=${params.schoolId}&examId=${params.examId}&scoreSection=${params.scoreSection}&minScore=${params.minScore}`, {}), //任课班级多科年级名次对比
  getScoreSection: (params) => http.get('/marking-statistics/exam-config/score/section', params), //任课班级多科总分分数段对比
  getMultiReportSign: examIds => http.post('/marking-statistics/multi-exam/list/final-sign', examIds), // 获取多科考试报告状态
}

export const datavApi = {
  // 区域驾驶舱接口相关
  getExamBaseDetails: schoolId => http.get(`/marking-exam-manager/exam-dashboard/examBase-statistics-detail?schoolId=${schoolId}`), // 区域驾驶舱-考试明细数据
  getExamSituationStatus: params => http.post(`/marking-platform/regions/region/exam-situation`, params), // 获取考情实时动态
  getSubjectRank: params => http.post(`/marking-statistics-report/multiple/basic/index/region/all/subject/excel/rate/ranking`, params), // 学科排行榜
  getSubjectBalance: params => http.post(`/marking-statistics-report/multiple/basic/index/region/all/subject/balance/degree`, params), // 各学科均衡度
  getSubjectsReport: params => http.post(`/marking-statistics/multi-exam/exam-dashboard/search`, params), // 获取多学科报告
  getSubjectsSummary: params => http.get(`/marking-statistics-report/multiple/basic/index/region/final/summary/all/school?finalExamId=${params.examId}&regionId=${params.schoolId}`), // 获取多科汇总成绩 所有学校基础指标分析数据
}


export const gradingApi = {
  startAll: (schoolId, examId) => http.post('/marking-exam-grading/grading-status/start/main', { schoolId, examId }), // 批阅总开始
  endAll: (schoolId, examId) => http.put('/marking-exam-grading/grading-status/finish?schoolId=' + schoolId + '&examId=' + examId), // 批阅总结束
  recover: (schoolId, examId) => http.post('/marking-exam-grading/grading-status/recover', { schoolId, examId }), // 恢复批阅
}


// WEBPACK FOOTER //
// ./src/v2/utils/api.js